import Swal from "sweetalert2";

function isDark(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

function themeToastColors() {
  const dark = isDark();
  return {
    background: dark ? "#0f0f12" : "#ffffff",
    color: dark ? "#fafafa" : "#18181b",
  };
}

function themeModalColors() {
  const dark = isDark();
  return {
    background: dark ? "#0f0f12" : "#ffffff",
    color: dark ? "#fafafa" : "#18181b",
    confirmButtonColor: "#fabbcb",
    cancelButtonColor: dark ? "#3f3f46" : "#e4e4e7",
  };
}

const toastDefaults = {
  toast: true as const,
  position: "top-end" as const,
  showConfirmButton: false,
  timer: 4200,
  timerProgressBar: true,
};

/** Toast-style notifications (stack top-right). */
export const notify = {
  success: (message: string) =>
    Swal.fire({
      ...toastDefaults,
      icon: "success",
      title: message,
      ...themeToastColors(),
    }),

  error: (message: string) =>
    Swal.fire({
      ...toastDefaults,
      icon: "error",
      title: message,
      ...themeToastColors(),
      timer: 6500,
    }),

  warning: (message: string) =>
    Swal.fire({
      ...toastDefaults,
      icon: "warning",
      title: message,
      ...themeToastColors(),
      timer: 5200,
    }),

  info: (message: string) =>
    Swal.fire({
      ...toastDefaults,
      icon: "info",
      title: message,
      ...themeToastColors(),
    }),

  /** Info toast that navigates / runs a callback when clicked. */
  infoClickable: (message: string, onClick: () => void, options?: { hint?: string }) =>
    Swal.fire({
      ...toastDefaults,
      icon: "info",
      title: message,
      text: options?.hint ?? "Click to open",
      timer: 8000,
      ...themeToastColors(),
      didOpen: (toast) => {
        toast.style.cursor = "pointer";
        toast.setAttribute("role", "link");
        toast.addEventListener("click", () => {
          Swal.close();
          onClick();
        });
      },
    }),
};

/** Modal confirm for destructive actions (replaces `window.confirm`). */
export async function confirmDanger(options: {
  title: string;
  text?: string;
  confirmButtonText?: string;
}): Promise<boolean> {
  const res = await Swal.fire({
    ...themeModalColors(),
    icon: "warning",
    title: options.title,
    text: options.text,
    showCancelButton: true,
    confirmButtonText: options.confirmButtonText ?? "Delete",
    cancelButtonText: "Cancel",
    reverseButtons: true,
    focusCancel: true,
    confirmButtonColor: "#dc2626",
  });
  return res.isConfirmed;
}

/** Modal confirm for impactful but non-destructive actions (SweetAlert2). */
export async function confirmAction(options: {
  title: string;
  text?: string;
  confirmButtonText?: string;
  icon?: "question" | "info" | "warning";
}): Promise<boolean> {
  const res = await Swal.fire({
    ...themeModalColors(),
    icon: options.icon ?? "question",
    title: options.title,
    text: options.text,
    showCancelButton: true,
    confirmButtonText: options.confirmButtonText ?? "Continue",
    cancelButtonText: "Cancel",
    reverseButtons: true,
    focusCancel: true,
  });
  return res.isConfirmed;
}

/** After member registration: temporary password is the last 6 phone digits. No email is sent. */
export async function alertAccountCreated(options: {
  userName: string;
  confirmButtonText?: string;
}): Promise<void> {
  const userName = escapeHtml(options.userName.trim() || "—");
  await Swal.fire({
    ...themeModalColors(),
    title: "",
    html: `
      <div style="text-align:center;padding-top:0.25rem">
        <div style="margin:0 auto;height:3.5rem;width:3.5rem;border-radius:9999px;background:#fde7f1;display:flex;align-items:center;justify-content:center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" stroke="#d54f8c" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 style="margin:1rem 0 0;font-size:1.25rem;font-weight:700;color:#171717">Account Created Successfully</h2>
        <p style="margin:0.35rem 0 0;font-size:0.875rem;color:#737373">Your account has been created.</p>
        <p style="margin:1.5rem 0 0;padding-bottom:0.75rem;border-bottom:1px solid #e5e5e5;text-align:left;font-size:0.875rem;color:#404040">
          Username: <strong style="color:#171717">${userName}</strong>
        </p>
        <p style="margin:1rem 0 0;text-align:left;font-size:0.875rem;font-weight:600;color:#171717">Temporary Password:</p>
        <p style="margin:0.5rem 0 0;text-align:left;font-size:0.875rem;line-height:1.5;color:#262626">The last 6 digits of your registered mobile number.</p>
        <p style="margin:1rem 0 0;text-align:left;font-size:0.875rem;color:#737373">You may change your password after your first login.</p>
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: options.confirmButtonText ?? "OK",
    allowOutsideClick: false,
    buttonsStyling: false,
    didOpen: () => {
      const popup = Swal.getPopup();
      if (popup) popup.style.borderRadius = "1.5rem";
      const btn = Swal.getConfirmButton();
      if (btn) {
        btn.style.width = "100%";
        btn.style.margin = "0.5rem 0 0";
        btn.style.border = "none";
        btn.style.borderRadius = "0.75rem";
        btn.style.background = "#d54f8c";
        btn.style.color = "#ffffff";
        btn.style.fontWeight = "600";
        btn.style.fontSize = "0.875rem";
        btn.style.padding = "0.85rem 1rem";
        btn.style.cursor = "pointer";
      }
      const actions = Swal.getActions();
      if (actions) {
        actions.style.width = "100%";
        actions.style.margin = "1rem 0 0";
      }
    },
  });
}

/** Blocking success modal (not a toast). */
export async function alertSuccessModal(options: {
  title: string;
  text?: string;
}): Promise<void> {
  await Swal.fire({
    ...themeModalColors(),
    icon: "success",
    title: options.title,
    text: options.text,
    confirmButtonText: "OK",
  });
}

/**
 * After admin password reset: show the temporary password with Copy / Email / Close.
 * Copy and Email keep the modal open so the password stays visible.
 */
export async function showTemporaryPasswordModal(options: {
  userName: string;
  temporaryPassword: string;
  email?: string | null;
  canEmail: boolean;
  onEmail?: () => Promise<void>;
}): Promise<void> {
  const emailHint = options.canEmail
    ? `Email will be sent to ${options.email?.trim() || "the member"}.`
    : "No email on file — copy and send manually.";

  await Swal.fire({
    ...themeModalColors(),
    icon: "success",
    title: "Temporary password created",
    html: `
      <div style="text-align:left;font-size:0.9rem;line-height:1.5">
        <p style="margin:0 0 0.75rem">Username: <strong>${escapeHtml(options.userName)}</strong></p>
        <p style="margin:0 0 0.35rem;font-size:0.75rem;text-transform:uppercase;letter-spacing:0.04em;opacity:0.7">Temporary password</p>
        <p style="margin:0;padding:0.75rem 0.9rem;border-radius:0.65rem;border:1px solid #f1d8e3;background:#fff5f9;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:1.05rem;font-weight:700;letter-spacing:0.02em;word-break:break-all">${escapeHtml(options.temporaryPassword)}</p>
        <p style="margin:0.85rem 0 0;font-size:0.8rem;opacity:0.75">${escapeHtml(emailHint)}</p>
        <p style="margin:0.5rem 0 0;font-size:0.8rem;opacity:0.75">Ask the member to sign in and change this password immediately.</p>
      </div>
    `,
    showDenyButton: options.canEmail && Boolean(options.onEmail),
    showCancelButton: true,
    confirmButtonText: "Copy password",
    denyButtonText: "Email password",
    cancelButtonText: "Close",
    denyButtonColor: "#d54f8c",
    reverseButtons: true,
    focusConfirm: true,
    allowOutsideClick: false,
    showLoaderOnDeny: true,
    preConfirm: async () => {
      try {
        await navigator.clipboard.writeText(options.temporaryPassword);
        const btn = Swal.getConfirmButton();
        if (btn) btn.textContent = "Copied";
      } catch {
        Swal.showValidationMessage("Could not copy password");
      }
      return false;
    },
    preDeny: async () => {
      if (!options.onEmail) return false;
      try {
        Swal.resetValidationMessage();
        await options.onEmail();
        const btn = Swal.getDenyButton();
        if (btn) {
          btn.textContent = "Emailed";
          btn.setAttribute("disabled", "true");
        }
      } catch (e) {
        Swal.showValidationMessage(
          e instanceof Error ? e.message : "Failed to email password"
        );
      }
      return false;
    },
  });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
